import { useState } from 'react'
import { translate, type Lang } from './i18n'
import type { IncidentFilter, IncidentPage } from './api'
import { fmtDateTime } from './time'
import { useAtlasWindows } from './shifts'

const TIERS = ['StreetCrime', 'PublicOrder', 'IndoorDomestic', 'Excluded']
const QUALITIES = ['Ok', 'Review', 'Unusable']

/**
 * Explorador de incidentes (§8.2).
 *
 * Lo que hay debajo de un hotspot. El analista llega aquí cuando un punto no cuadra con lo que
 * conoce de la calle y quiere ver las filas que lo produjeron, antes de creerle al cálculo.
 *
 * Los resultados se pintan en el mismo mapa que los hotspots, en pequeño y debajo: la pregunta es
 * si el punto tiene detrás lo que dice tener.
 */
export function IncidentExplorer({
  lang, page, busy, onSearch, selected = null, onSelect,
}: {
  lang: Lang
  page: IncidentPage | null
  busy: boolean
  onSearch: (filter: IncidentFilter) => void
  /** El incidente elegido: el mapa va a él y lo resalta. */
  selected?: string | null
  onSelect?: (incidentId: string) => void
}) {
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [tier, setTier] = useState('')
  const [quality, setQuality] = useState('')
  // La franja del turno, como la del cálculo: lo que hay debajo de los hotspots de 06-10 son los
  // incidentes de 06-10, no los del día entero (H-89).
  const ventanas = useAtlasWindows()
  const [window, setWindow] = useState('ALL_DAY')
  const faltaPeriodo = window !== 'ALL_DAY' && (!from || !to)

  const cuando = (iso: string | null) =>
    iso ? fmtDateTime(iso, lang) : '—'

  return (
    <section className="panel">
      <h2>{translate(lang, 'explorer.title')}</h2>
      <p className="notice">{translate(lang, 'explorer.help')}</p>

      <div className="card">
        <label className="rule">
          {translate(lang, 'explorer.from')}
          <input type="date" value={from} disabled={busy} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label className="rule">
          {translate(lang, 'explorer.to')}
          <input type="date" value={to} disabled={busy} onChange={(e) => setTo(e.target.value)} />
        </label>
        <label className="rule">
          {translate(lang, 'explorer.tier')}
          <select value={tier} disabled={busy} onChange={(e) => setTier(e.target.value)}>
            <option value="">{translate(lang, 'explorer.any')}</option>
            {TIERS.map((x) => <option key={x} value={x}>{translate(lang, `tier.${x}`)}</option>)}
          </select>
        </label>
        <label className="rule">
          {translate(lang, 'explorer.quality')}
          <select value={quality} disabled={busy} onChange={(e) => setQuality(e.target.value)}>
            <option value="">{translate(lang, 'explorer.any')}</option>
            {QUALITIES.map((x) => (
              <option key={x} value={x}>{translate(lang, `qualityStatus.${x}`)}</option>
            ))}
          </select>
        </label>

        <label className="rule">
          {translate(lang, 'explorer.window')}
          <select value={window} disabled={busy} onChange={(e) => setWindow(e.target.value)}>
            {ventanas.map((w) => (
              <option key={w} value={w}>{w === 'ALL_DAY' ? translate(lang, 'explorer.allDay') : w}</option>
            ))}
          </select>
        </label>

        <div className="actions">
          <button
            className="primary"
            disabled={busy || faltaPeriodo}
            onClick={() => onSearch({ from, to, tier, quality, window })}
          >
            {translate(lang, 'explorer.search')}
          </button>
        </div>
        {faltaPeriodo && <p className="meta">{translate(lang, 'explorer.windowNeedsPeriod')}</p>}
      </div>

      {page && (
        <div className="card">
          {/* Cuántos hay y cuántos se ven son dos números distintos, y se dicen los dos: un mapa
              recortado que no avisa que está recortado miente sobre el tamaño del problema. */}
          <p className={page.shown < page.matching ? 'error' : 'notice'}>
            {page.shown < page.matching
              ? translate(lang, 'explorer.truncated', [page.shown, page.matching])
              : translate(lang, 'explorer.found', [page.matching])}
          </p>

          <div className="rows">
            {page.incidents.slice(0, 40).map((i) => (
              <div
                key={i.incidentId}
                className={`row-line ${selected === i.incidentId ? 'chosen' : ''}`}
                // Tocar una fila lleva el mapa al incidente. Sin coordenada no hay adónde ir.
                {...(onSelect && i.latitude !== null && i.longitude !== null ? {
                  role: 'button', tabIndex: 0, title: translate(lang, 'incidents.showOnMap'),
                  onClick: () => onSelect(i.incidentId),
                  onKeyDown: (e: React.KeyboardEvent) => { if (e.key === 'Enter') onSelect(i.incidentId) },
                  style: { cursor: 'pointer' },
                } : {})}
              >
                <span className="meta">{cuando(i.occurredAt)}</span>
                {!i.exact && <span className="meta" title={translate(lang, 'explorer.inexactHelp')}>≈</span>}
                <span className="grow">
                  {i.offenseName}
                  {i.blockAddress ? ` · ${i.blockAddress}` : ''}
                </span>
                <span className="meta">{i.beat}</span>
              </div>
            ))}
          </div>
          {page.incidents.length > 40 && (
            <p className="notice">{translate(lang, 'explorer.listCut', [page.incidents.length])}</p>
          )}
        </div>
      )}
    </section>
  )
}
